// MUI Imports
import type { Theme } from '@mui/material/styles'

// Type Imports
import type { Skin, SystemMode } from '@core/types'

// Config Imports
import theme from '@configs/theme'

// Palette Imports
import { channels, paletteOf } from './palette'

/**
 * MUI's two color schemes, built from `src/configs/theme.ts`.
 *
 * Only brand decisions (the six colors, the background and the paper) come
 * from the theme file. The auxiliary tones below — avatar background, tooltip
 * background, progress track — are design system detail and change with the
 * mode, not with the brand.
 */
const AUXILIARY = {
  light: {
    avatarBg: '#E4E9F2',
    inverted: '#16233F', // snackbar and tooltip use the color opposite to the background
    chatBg: '#EEF2F9',
    greyLightBg: '#F7F9FC',
    trackBg: '#E2E8F2'
  },
  dark: {
    avatarBg: '#2B3E68',
    inverted: '#F2F5FB',
    chatBg: '#17253F',
    greyLightBg: '#26375C',
    trackBg: '#2E4166'
  }
} as const

/**
 * How much of the brand channel each line of text carries — measured, not
 * guessed. Over the light paper these land at 10:1, 6.2:1 and 4.2:1, so the
 * third level is still a readable caption instead of a hint of one; the dark
 * scheme needs less opacity to reach the same place, and says so here rather
 * than inheriting a number tuned for white paper.
 *
 * `disabled` is the third level of text in this product — dates, counters,
 * section labels — and not the disabled state: that one is `action.disabled`,
 * plus the `opacity` the overrides put on `.Mui-disabled`.
 */
const TEXT_OPACITY = {
  light: { primary: 0.9, secondary: 0.8, disabled: 0.68 },
  dark: { primary: 0.9, secondary: 0.78, disabled: 0.6 }
} as const

/**
 * The lines: what separates two surfaces, and what draws the edge of a control.
 * A divider only has to be seen; an input border has to be found, so it is much
 * stronger — at 0.12 the old one sat at 1.25:1 against the paper and the fields
 * read as painted-on rectangles.
 */
const LINE_OPACITY = {
  light: { divider: 0.2, inputBorder: 0.45 },
  dark: { divider: 0.22, inputBorder: 0.42 }
} as const

const scheme = (mode: SystemMode, skin: Skin) => {
  const { background, paper } = mode === 'light' ? theme.surfaces.light : theme.surfaces.dark
  const aux = AUXILIARY[mode]
  const text = TEXT_OPACITY[mode]
  const line = LINE_OPACITY[mode]

  // Channel behind text, dividers and actions: a blue-grey the theme defines
  // in `mainColorChannels`, which here only takes opacity.
  const channel = `var(--mui-mainColorChannels-${mode})`
  const over = (opacity: number) => `rgb(${channel} / ${opacity})`

  // The tonal text tone is measured against the surface it will be read on, so
  // the same brand color answers differently in each scheme.
  const colorOf = (color: Parameters<typeof paletteOf>[0], name: string) => paletteOf(color, name, paper)

  return {
    palette: {
      primary: colorOf(theme.colors.primary, 'primary'),
      secondary: colorOf(theme.colors.secondary, 'secondary'),
      error: colorOf(theme.colors.error, 'error'),
      warning: colorOf(theme.colors.warning, 'warning'),
      info: colorOf(theme.colors.info, 'info'),
      success: colorOf(theme.colors.success, 'success'),
      text: {
        primary: over(text.primary),
        secondary: over(text.secondary),
        disabled: over(text.disabled),
        primaryChannel: channel,
        secondaryChannel: channel
      },
      divider: over(line.divider),
      dividerChannel: channel,
      background: {
        // On the bordered skin the body rises to the paper level and what
        // separates the surfaces is the border, not the difference in shade.
        default: skin === 'bordered' ? paper : background,
        paper,
        paperChannel: channels(paper)
      },
      action: {
        active: over(0.68),
        hover: over(0.07),
        selected: over(0.1),
        disabled: over(0.32),
        disabledBackground: over(0.16),
        focus: over(0.12),
        focusOpacity: 0.12,
        activeChannel: channel,
        selectedChannel: channel
      },
      Alert: {
        // The text of a standard alert sits on the tonal background, so it
        // takes the measured tone — not the fill color.
        errorColor: 'var(--mui-palette-error-tonalText)',
        warningColor: 'var(--mui-palette-warning-tonalText)',
        infoColor: 'var(--mui-palette-info-tonalText)',
        successColor: 'var(--mui-palette-success-tonalText)',
        errorStandardBg: 'var(--mui-palette-error-lightOpacity)',
        warningStandardBg: 'var(--mui-palette-warning-lightOpacity)',
        infoStandardBg: 'var(--mui-palette-info-lightOpacity)',
        successStandardBg: 'var(--mui-palette-success-lightOpacity)',
        errorFilledColor: 'var(--mui-palette-error-contrastText)',
        warningFilledColor: 'var(--mui-palette-warning-contrastText)',
        infoFilledColor: 'var(--mui-palette-info-contrastText)',
        successFilledColor: 'var(--mui-palette-success-contrastText)',
        errorFilledBg: 'var(--mui-palette-error-main)',
        warningFilledBg: 'var(--mui-palette-warning-main)',
        infoFilledBg: 'var(--mui-palette-info-main)',
        successFilledBg: 'var(--mui-palette-success-main)'
      },
      Avatar: {
        defaultBg: aux.avatarBg
      },
      Chip: {
        defaultBorder: 'var(--mui-palette-divider)'
      },
      FilledInput: {
        bg: 'var(--mui-palette-action-hover)',
        hoverBg: 'var(--mui-palette-action-selected)',
        disabledBg: 'var(--mui-palette-action-hover)'
      },
      SnackbarContent: {
        bg: aux.inverted,
        color: 'var(--mui-palette-background-paper)'
      },
      Switch: {
        defaultColor: 'var(--mui-palette-common-white)',
        defaultDisabledColor: 'var(--mui-palette-common-white)',
        primaryDisabledColor: 'var(--mui-palette-common-white)',
        secondaryDisabledColor: 'var(--mui-palette-common-white)',
        errorDisabledColor: 'var(--mui-palette-common-white)',
        warningDisabledColor: 'var(--mui-palette-common-white)',
        infoDisabledColor: 'var(--mui-palette-common-white)',
        successDisabledColor: 'var(--mui-palette-common-white)'
      },
      Tooltip: {
        bg: aux.inverted
      },
      TableCell: {
        border: 'var(--mui-palette-divider)'
      },
      customColors: {
        bodyBg: background,
        chatBg: aux.chatBg,
        greyLightBg: aux.greyLightBg,
        inputBorder: over(line.inputBorder),
        tableHeaderBg: paper,
        tooltipText: paper,
        trackBg: aux.trackBg
      }
    }
  }
}

const colorSchemes = (skin: Skin): Theme['colorSchemes'] =>
  ({
    light: scheme('light', skin),
    dark: scheme('dark', skin)
  }) as Theme['colorSchemes']

export default colorSchemes
